/**
 * Revenue Helpers — pure data-shaping utilities for the User Compensation
 * > Company Revenue tab and its XLSX export.
 *
 * This module must NOT import from UserCompensation.tsx or any component file.
 * It defines its own structural types for DateRange and PeriodPreset and accepts
 * injected callbacks for any business logic that lives in the component.
 */

import { format } from 'date-fns';
import * as ExcelJS from 'exceljs';
import { parseAsLocalDate } from '@/utils/dateHelpers';

// ============================================================================
// LOCAL TYPE DEFINITIONS (own structural DateRange/preset types, no component import)
// ============================================================================

export interface RevenueDateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export type RevenuePeriodPreset =
  | 'mtd'
  | 'ytd'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'last_year'
  | 'custom';

// ============================================================================
// MINIMAL STRUCTURAL INTERFACE
// ============================================================================

/** Fields consumed by filtering, summary building, and detail-row building. */
export interface RevenuePayoutLike {
  loanNumber: string;
  borrowerName: string;
  loName: string;
  type: 'Banked' | 'Brokered' | 'Override' | 'Mentor';
  loanAmount: number;
  companyRevenue: number;
  grossCheckReceived: number;
  processingFee: number;
  eAndO: number;
  revenueDate: string;
  fundDate: string;
  purchaseDate: string | null;
}

// ============================================================================
// ROW MODELS
// ============================================================================

export interface RevenueSummaryRow {
  individualName: string;
  loanCount: number;
  brokeredCompanyRevenue: number;
  bankedCompanyRevenue: number;
  bankedNetProfit: number;
  totalCompanyNetProfit: number;
  percentOfTotal: number;
}

export interface ChannelComparisonRow {
  metric: string;
  brokered: number;
  banked: number;
  total: number;
}

export interface MonthlyComparisonRow {
  month: string;           // e.g. "Jan 2026"
  units: number;
  loanVolume: number;
  brokeredRevenue: number;
  bankedNetProfit: number;
  totalNetProfit: number;
  totalNetProfitMom: number | null;   // MoM % change in totalNetProfit
  avgNetProfitPerLoan: number;
  avgNetProfitMom: number | null;     // MoM % change in avgNetProfitPerLoan
}

export interface RevenueDetailRow {
  type: string;
  lo: string;
  loanNumber: string;
  borrower: string;
  revenueDate: string;
  fundDate: string;
  purchaseDate: string;
  loanAmount: number;
  companyRevenue: number;
  bankedNetProfit: number;
  grossCheckReceived: number;
  processingFee: number;
  eAndO: number;
}

// ============================================================================
// PURE HELPERS
// ============================================================================

/**
 * Filter payouts by inclusive date range against payout.revenueDate.
 * Returns all payouts when both bounds are undefined.
 */
export function filterRevenuePayoutsByDateRange<T extends RevenuePayoutLike>(
  payouts: T[],
  dateRange: RevenueDateRange
): T[] {
  if (!dateRange.from && !dateRange.to) {
return payouts;
}
  return payouts.filter((p) => {
    const d = parseAsLocalDate(p.revenueDate);
    if (dateRange.from && d < dateRange.from) {
return false;
}
    if (dateRange.to && d > dateRange.to) {
return false;
}
    return true;
  });
}

/**
 * Human-readable label for the active period, used in the tab UI and export
 * header rows.  Handles both-sided, one-sided, and empty custom ranges.
 */
export function formatRevenuePeriodLabel(
  dateRange: RevenueDateRange,
  preset: RevenuePeriodPreset
): string {
  const PRESET_LABELS: Record<RevenuePeriodPreset, string> = {
    mtd: 'Month to Date',
    ytd: 'Year to Date',
    last_month: 'Last Month',
    this_quarter: 'This Quarter',
    last_quarter: 'Last Quarter',
    last_year: 'Last Year',
    custom: 'Custom'
  };

  if (preset !== 'custom') {
return PRESET_LABELS[preset];
}

  if (dateRange.from && dateRange.to) {
    return `${format(dateRange.from, 'MMM d, yyyy')} \u2013 ${format(dateRange.to, 'MMM d, yyyy')}`;
  }
  if (dateRange.from) {
    return `From ${format(dateRange.from, 'MMM d, yyyy')}`;
  }
  if (dateRange.to) {
    return `Through ${format(dateRange.to, 'MMM d, yyyy')}`;
  }
  return 'All Time';
}

/**
 * Aggregate payouts into one summary row per LO.
 * Total Company Net Profit = Brokered Company Revenue + Banked Net Profit.
 * Sorted by Total Company Net Profit descending.
 * Each row includes its percentage of the grand total.
 */
export function buildRevenueSummaryRows<T extends RevenuePayoutLike>(
  payouts: T[],
  getBankedNetProfit: (payout: T) => number
): RevenueSummaryRow[] {
  const byLo = new Map<string, T[]>();
  for (const p of payouts) {
    const existing = byLo.get(p.loName);
    if (existing) {
      existing.push(p);
    } else {
      byLo.set(p.loName, [p]);
    }
  }

  const rows: RevenueSummaryRow[] = [];
  for (const [loName, loPayouts] of byLo) {
    const brokered = loPayouts.filter((p) => p.type === 'Brokered');
    const banked = loPayouts.filter((p) => p.type === 'Banked');
    const loanNumbers = new Set(loPayouts.map((p) => p.loanNumber));

    const brokeredCompanyRevenue = brokered.reduce((s, p) => s + p.companyRevenue, 0);
    const bankedCompanyRevenue = banked.reduce((s, p) => s + p.companyRevenue, 0);
    const bankedNetProfit = banked.reduce((s, p) => s + getBankedNetProfit(p), 0);

    rows.push({
      individualName: loName,
      loanCount: loanNumbers.size,
      brokeredCompanyRevenue,
      bankedCompanyRevenue,
      bankedNetProfit,
      totalCompanyNetProfit: brokeredCompanyRevenue + bankedNetProfit,
      percentOfTotal: 0 // filled below once grand total is known
    });
  }

  const grandTotal = rows.reduce((s, r) => s + r.totalCompanyNetProfit, 0);
  for (const row of rows) {
    row.percentOfTotal = grandTotal !== 0 ? row.totalCompanyNetProfit / grandTotal : 0;
  }

  rows.sort((a, b) => b.totalCompanyNetProfit - a.totalCompanyNetProfit);
  return rows;
}

/**
 * Flatten payouts into detail rows.
 * Brokered-only fields (grossCheckReceived, processingFee) are zeroed for banked rows.
 * Sorted by Revenue Date descending, then LO, then Loan Number.
 */
export function buildRevenueDetailRows<T extends RevenuePayoutLike>(
  payouts: T[],
  getBankedNetProfit: (payout: T) => number
): RevenueDetailRow[] {
  const rows: RevenueDetailRow[] = payouts.map((p) => {
    const isBrokered = p.type === 'Brokered';
    return {
      type: p.type,
      lo: p.loName,
      loanNumber: p.loanNumber,
      borrower: p.borrowerName,
      revenueDate: p.revenueDate,
      fundDate: p.fundDate,
      purchaseDate: p.purchaseDate ?? '',
      loanAmount: p.loanAmount,
      companyRevenue: p.companyRevenue,
      bankedNetProfit: isBrokered ? 0 : getBankedNetProfit(p),
      grossCheckReceived: isBrokered ? p.grossCheckReceived : 0,
      processingFee: isBrokered ? p.processingFee : 0,
      eAndO: p.eAndO
    };
  });

  rows.sort((a, b) => {
    // Revenue date descending
    if (a.revenueDate !== b.revenueDate) {
return b.revenueDate.localeCompare(a.revenueDate);
}
    // LO ascending
    if (a.lo !== b.lo) {
return a.lo.localeCompare(b.lo);
}
    // Loan number ascending
    return a.loanNumber.localeCompare(b.loanNumber);
  });

  return rows;
}

/**
 * Build channel comparison rows: brokered vs banked side by side.
 * Each row is a metric with brokered, banked, and total columns.
 */
export function buildChannelComparisonRows<T extends RevenuePayoutLike>(
  payouts: T[],
  getBankedNetProfit: (payout: T) => number
): ChannelComparisonRow[] {
  const brokered = payouts.filter((p) => p.type === 'Brokered');
  const banked = payouts.filter((p) => p.type === 'Banked');

  const brokeredLoanNums = new Set(brokered.map((p) => p.loanNumber));
  const bankedLoanNums = new Set(banked.map((p) => p.loanNumber));
  const allLoanNums = new Set(payouts.map((p) => p.loanNumber));

  const brokeredUnits = brokeredLoanNums.size;
  const bankedUnits = bankedLoanNums.size;
  const totalUnits = allLoanNums.size;

  const brokeredVolume = brokered.reduce((s, p) => s + p.loanAmount, 0);
  const bankedVolume = banked.reduce((s, p) => s + p.loanAmount, 0);

  const brokeredRevenue = brokered.reduce((s, p) => s + p.companyRevenue, 0);
  const bankedRevenue = banked.reduce((s, p) => s + p.companyRevenue, 0);

  const bankedNetProfit = banked.reduce((s, p) => s + getBankedNetProfit(p), 0);

  const brokeredNetProfit = brokeredRevenue; // for brokered, company revenue IS the net profit
  const totalNetProfit = brokeredNetProfit + bankedNetProfit;

  const brokeredAvgPerLoan = brokeredUnits > 0 ? brokeredNetProfit / brokeredUnits : 0;
  const bankedAvgPerLoan = bankedUnits > 0 ? bankedNetProfit / bankedUnits : 0;
  const totalAvgPerLoan = totalUnits > 0 ? totalNetProfit / totalUnits : 0;

  const brokeredAvgLoanAmt = brokeredUnits > 0 ? brokeredVolume / brokeredUnits : 0;
  const bankedAvgLoanAmt = bankedUnits > 0 ? bankedVolume / bankedUnits : 0;
  const totalAvgLoanAmt = totalUnits > 0 ? (brokeredVolume + bankedVolume) / totalUnits : 0;

  const brokeredMargin = brokeredVolume > 0 ? brokeredNetProfit / brokeredVolume : 0;
  const bankedMargin = bankedVolume > 0 ? bankedNetProfit / bankedVolume : 0;
  const totalMargin = (brokeredVolume + bankedVolume) > 0
    ? totalNetProfit / (brokeredVolume + bankedVolume) : 0;

  return [
    { metric: 'Units', brokered: brokeredUnits, banked: bankedUnits, total: totalUnits },
    { metric: 'Loan Volume', brokered: brokeredVolume, banked: bankedVolume, total: brokeredVolume + bankedVolume },
    { metric: 'Company Revenue', brokered: brokeredRevenue, banked: bankedRevenue, total: brokeredRevenue + bankedRevenue },
    { metric: 'Net Profit', brokered: brokeredNetProfit, banked: bankedNetProfit, total: totalNetProfit },
    { metric: 'Avg Net Profit / Loan', brokered: brokeredAvgPerLoan, banked: bankedAvgPerLoan, total: totalAvgPerLoan },
    { metric: 'Avg Loan Amount', brokered: brokeredAvgLoanAmt, banked: bankedAvgLoanAmt, total: totalAvgLoanAmt },
    { metric: 'Net Margin (Profit / Volume)', brokered: brokeredMargin, banked: bankedMargin, total: totalMargin }
  ];
}

/**
 * Build monthly comparison rows sorted chronologically.
 * Groups payouts by YYYY-MM from revenueDate.
 */
export function buildMonthlyComparisonRows<T extends RevenuePayoutLike>(
  payouts: T[],
  getBankedNetProfit: (payout: T) => number
): MonthlyComparisonRow[] {
  const byMonth = new Map<string, T[]>();
  for (const p of payouts) {
    // revenueDate is YYYY-MM-DD — take first 7 chars for YYYY-MM key
    const key = p.revenueDate.slice(0, 7);
    const existing = byMonth.get(key);
    if (existing) {
      existing.push(p);
    } else {
      byMonth.set(key, [p]);
    }
  }

  const rows: MonthlyComparisonRow[] = [];
  for (const [yearMonth, monthPayouts] of byMonth) {
    const brokered = monthPayouts.filter((p) => p.type === 'Brokered');
    const banked = monthPayouts.filter((p) => p.type === 'Banked');
    const loanNums = new Set(monthPayouts.map((p) => p.loanNumber));
    const units = loanNums.size;

    const loanVolume = monthPayouts.reduce((s, p) => s + p.loanAmount, 0);
    const brokeredRevenue = brokered.reduce((s, p) => s + p.companyRevenue, 0);
    const bankedNP = banked.reduce((s, p) => s + getBankedNetProfit(p), 0);
    const totalNetProfit = brokeredRevenue + bankedNP;

    // Format YYYY-MM to "Mon YYYY"
    const [y, m] = yearMonth.split('-');
    const monthDate = new Date(Number(y), Number(m) - 1, 1);
    const monthLabel = format(monthDate, 'MMM yyyy');

    rows.push({
      month: monthLabel,
      units,
      loanVolume,
      brokeredRevenue,
      bankedNetProfit: bankedNP,
      totalNetProfit,
      totalNetProfitMom: null,
      avgNetProfitPerLoan: units > 0 ? totalNetProfit / units : 0,
      avgNetProfitMom: null
    });
  }

  // Sort chronologically by the YYYY-MM key (Map preserves insertion order but keys may be unordered)
  const sortedKeys = [...byMonth.keys()].sort();
  const keyToIndex = new Map(sortedKeys.map((k, i) => {
    const [y, m] = k.split('-');
    return [format(new Date(Number(y), Number(m) - 1, 1), 'MMM yyyy'), i];
  }));
  rows.sort((a, b) => (keyToIndex.get(a.month) ?? 0) - (keyToIndex.get(b.month) ?? 0));

  // Compute month-over-month % change
  for (let i = 0; i < rows.length; i++) {
    if (i === 0) {
continue;
}
    const prevNP = rows[i - 1].totalNetProfit;
    const prevAvg = rows[i - 1].avgNetProfitPerLoan;
    rows[i].totalNetProfitMom = prevNP !== 0 ? (rows[i].totalNetProfit - prevNP) / prevNP : null;
    rows[i].avgNetProfitMom = prevAvg !== 0 ? (rows[i].avgNetProfitPerLoan - prevAvg) / prevAvg : null;
  }

  return rows;
}

// ============================================================================
// WORKBOOK / DOWNLOAD WRAPPER
// ============================================================================

interface ExportRevenueWorkbookInput {
  summaryRows: RevenueSummaryRow[];
  detailRows: RevenueDetailRow[];
  channelRows: ChannelComparisonRow[];
  monthlyRows: MonthlyComparisonRow[];
  periodLabel: string;
}

export async function exportRevenueWorkbook({
  summaryRows,
  detailRows,
  channelRows,
  monthlyRows,
  periodLabel
}: ExportRevenueWorkbookInput): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'LendWise';
  workbook.created = new Date();

  // ── Shared style palette ──
  const currencyFmt = '$#,##0.00';
  const pctFmt = '0.0%';
  const accentFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1B2A4A' } };
  const accentFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
  const subtitleFont: Partial<ExcelJS.Font> = { size: 10, italic: true, color: { argb: 'FF8899AA' } };
  const subtitleFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF253D5B' } };
  const zebra1: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F9FB' } };
  const zebra2: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
  const totalFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
  const totalBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'medium', color: { argb: 'FF1B2A4A' } },
    bottom: { style: 'double', color: { argb: 'FF1B2A4A' } }
  };
  const titleCellFont: Partial<ExcelJS.Font> = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };

  /** Apply the standard title bar (row 1) + period subtitle (row 2) to a sheet. */
  const applyTitleBlock = (ws: ExcelJS.Worksheet, title: string, colCount: number) => {
    const lastCol = String.fromCharCode(64 + colCount); // A=1, M=13 etc.
    ws.mergeCells(`A1:${lastCol}1`);
    const tc = ws.getCell('A1');
    tc.value = title;
    tc.font = titleCellFont;
    tc.fill = accentFill;
    tc.alignment = { vertical: 'middle', horizontal: 'left' };
    ws.getRow(1).height = 30;

    ws.mergeCells(`A2:${lastCol}2`);
    const pc = ws.getCell('A2');
    pc.value = `Period: ${periodLabel}`;
    pc.font = subtitleFont;
    pc.fill = subtitleFill;
    pc.alignment = { vertical: 'middle', horizontal: 'left' };
  };

  /** Apply header row styling at the given row number. */
  const applyHeaderRow = (ws: ExcelJS.Worksheet, rowNum: number, headers: string[]) => {
    const hr = ws.getRow(rowNum);
    headers.forEach((h, i) => {
      const cell = hr.getCell(i + 1);
      cell.value = h;
      cell.font = accentFont;
      cell.fill = accentFill;
      cell.alignment = { horizontal: i === 0 ? 'left' : 'right', vertical: 'middle' };
    });
    hr.height = 22;
  };

  /** Apply zebra + total styling to a data block. */
  const applyTotalRow = (ws: ExcelJS.Worksheet, rowNum: number, colCount: number) => {
    for (let c = 1; c <= colCount; c++) {
      const cell = ws.getRow(rowNum).getCell(c);
      cell.font = { bold: true };
      cell.fill = totalFill;
      cell.border = totalBorder;
    }
  };

  // ==========================================================================
  // By Individual sheet
  // ==========================================================================
  const summary = workbook.addWorksheet('By Individual');
  const sumCols = 7;
  applyTitleBlock(summary, 'Company Net Profit — By Individual', sumCols);

  const summaryHeaders = [
    'Individual Name', 'Loan Count', 'Brokered Company Revenue',
    'Banked Company Revenue', 'Banked Net Profit', 'Total Company Net Profit', '% of Total'
  ];
  applyHeaderRow(summary, 4, summaryHeaders);

  summary.getColumn(1).width = 28;
  summary.getColumn(2).width = 12;
  for (let c = 3; c <= 6; c++) {
 summary.getColumn(c).width = 24; summary.getColumn(c).numFmt = currencyFmt; 
}
  summary.getColumn(7).width = 14; summary.getColumn(7).numFmt = pctFmt;

  let summaryRowIdx = 5;
  for (let i = 0; i < summaryRows.length; i++) {
    const row = summaryRows[i];
    const r = summary.getRow(summaryRowIdx++);
    r.getCell(1).value = row.individualName;
    r.getCell(1).font = { bold: true };
    r.getCell(2).value = row.loanCount;
    r.getCell(3).value = row.brokeredCompanyRevenue;
    r.getCell(4).value = row.bankedCompanyRevenue;
    r.getCell(5).value = row.bankedNetProfit;
    r.getCell(6).value = row.totalCompanyNetProfit;
    r.getCell(7).value = row.percentOfTotal;
    const stripe = i % 2 === 0 ? zebra1 : zebra2;
    for (let c = 1; c <= sumCols; c++) {
r.getCell(c).fill = stripe;
}
  }

  // Total row
  const st = summary.getRow(summaryRowIdx);
  st.getCell(1).value = 'TOTAL';
  st.getCell(2).value = summaryRows.reduce((s, r) => s + r.loanCount, 0);
  st.getCell(3).value = summaryRows.reduce((s, r) => s + r.brokeredCompanyRevenue, 0);
  st.getCell(4).value = summaryRows.reduce((s, r) => s + r.bankedCompanyRevenue, 0);
  st.getCell(5).value = summaryRows.reduce((s, r) => s + r.bankedNetProfit, 0);
  st.getCell(6).value = summaryRows.reduce((s, r) => s + r.totalCompanyNetProfit, 0);
  st.getCell(7).value = summaryRows.length > 0 ? 1 : 0;
  applyTotalRow(summary, summaryRowIdx, sumCols);

  // ==========================================================================
  // Loan Detail sheet
  // ==========================================================================
  const detail = workbook.addWorksheet('Loan Detail');
  const detCols = 13;
  applyTitleBlock(detail, 'Company Net Profit — Loan Detail', detCols);

  const detailHeaders = [
    'Type', 'LO', 'Loan Number', 'Borrower', 'Revenue Date', 'Fund Date',
    'Purchase Date', 'Loan Amount', 'Company Revenue', 'Banked Net Profit',
    'Gross Check Received', 'Processing Fee', 'E&O'
  ];
  applyHeaderRow(detail, 4, detailHeaders);

  const detailWidths = [10, 22, 16, 24, 14, 14, 14, 16, 18, 18, 20, 14, 10];
  detailWidths.forEach((w, i) => {
 detail.getColumn(i + 1).width = w; 
});
  for (let c = 8; c <= 13; c++) {
detail.getColumn(c).numFmt = currencyFmt;
}

  let detailRowIdx = 5;
  for (let i = 0; i < detailRows.length; i++) {
    const row = detailRows[i];
    const r = detail.getRow(detailRowIdx++);
    r.getCell(1).value = row.type;
    r.getCell(2).value = row.lo;
    r.getCell(3).value = row.loanNumber;
    r.getCell(4).value = row.borrower;
    r.getCell(5).value = row.revenueDate;
    r.getCell(6).value = row.fundDate;
    r.getCell(7).value = row.purchaseDate;
    r.getCell(8).value = row.loanAmount;
    r.getCell(9).value = row.companyRevenue;
    r.getCell(10).value = row.bankedNetProfit;
    r.getCell(11).value = row.grossCheckReceived;
    r.getCell(12).value = row.processingFee;
    r.getCell(13).value = row.eAndO;
    const stripe = i % 2 === 0 ? zebra1 : zebra2;
    for (let c = 1; c <= detCols; c++) {
r.getCell(c).fill = stripe;
}
  }

  const dt = detail.getRow(detailRowIdx);
  dt.getCell(1).value = 'TOTAL';
  dt.getCell(8).value = detailRows.reduce((s, r) => s + r.loanAmount, 0);
  dt.getCell(9).value = detailRows.reduce((s, r) => s + r.companyRevenue, 0);
  dt.getCell(10).value = detailRows.reduce((s, r) => s + r.bankedNetProfit, 0);
  dt.getCell(11).value = detailRows.reduce((s, r) => s + r.grossCheckReceived, 0);
  dt.getCell(12).value = detailRows.reduce((s, r) => s + r.processingFee, 0);
  dt.getCell(13).value = detailRows.reduce((s, r) => s + r.eAndO, 0);
  applyTotalRow(detail, detailRowIdx, detCols);

  // ==========================================================================
  // Channel Comparison sheet
  // ==========================================================================
  const channel = workbook.addWorksheet('Channel Comparison');
  const chCols = 4;
  applyTitleBlock(channel, 'Brokered vs Banked — Channel Comparison', chCols);
  applyHeaderRow(channel, 4, ['Metric', 'Brokered', 'Banked', 'Total']);

  channel.getColumn(1).width = 30;
  channel.getColumn(2).width = 22;
  channel.getColumn(3).width = 22;
  channel.getColumn(4).width = 22;

  const MARGIN_METRIC = 'Net Margin (Profit / Volume)';
  const UNITS_METRIC = 'Units';
  let channelRowIdx = 5;
  for (let i = 0; i < channelRows.length; i++) {
    const row = channelRows[i];
    const r = channel.getRow(channelRowIdx++);
    r.getCell(1).value = row.metric;
    r.getCell(1).font = { bold: true };
    r.getCell(2).value = row.brokered;
    r.getCell(3).value = row.banked;
    r.getCell(4).value = row.total;

    if (row.metric === MARGIN_METRIC) {
      for (let c = 2; c <= 4; c++) {
r.getCell(c).numFmt = pctFmt;
}
    } else if (row.metric !== UNITS_METRIC) {
      for (let c = 2; c <= 4; c++) {
r.getCell(c).numFmt = currencyFmt;
}
    }

    const stripe = i % 2 === 0 ? zebra1 : zebra2;
    for (let c = 1; c <= chCols; c++) {
r.getCell(c).fill = stripe;
}
  }

  // ==========================================================================
  // Monthly Comparison sheet
  // ==========================================================================
  const monthly = workbook.addWorksheet('Monthly Comparison');
  const moCols = 9;
  const sectionFont: Partial<ExcelJS.Font> = { bold: true, size: 10, color: { argb: 'FF1B2A4A' } };
  const greenFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FF1B7A3D' } };
  const redFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFC41E3A' } };
  const greenFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
  const redFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE4EC' } };

  applyTitleBlock(monthly, 'Monthly Revenue Performance', moCols);

  // Section group headers (row 3)
  monthly.mergeCells('F3:G3');
  const npGroupCell = monthly.getCell('F3');
  npGroupCell.value = 'Net Profit';
  npGroupCell.font = sectionFont;
  npGroupCell.alignment = { horizontal: 'center' };
  npGroupCell.border = { bottom: { style: 'thin', color: { argb: 'FF1B2A4A' } } };

  monthly.mergeCells('H3:I3');
  const avgGroupCell = monthly.getCell('H3');
  avgGroupCell.value = 'Avg Per Loan';
  avgGroupCell.font = sectionFont;
  avgGroupCell.alignment = { horizontal: 'center' };
  avgGroupCell.border = { bottom: { style: 'thin', color: { argb: 'FF1B2A4A' } } };

  const monthlyHeaders = [
    'Month', 'Units', 'Loan Volume', 'Brokered Revenue', 'Banked Net Profit',
    'Total Net Profit', 'MoM %', 'Avg Net Profit / Loan', 'MoM %'
  ];

  applyHeaderRow(monthly, 4, monthlyHeaders);

  // Column widths and formats
  monthly.getColumn(1).width = 14;
  monthly.getColumn(2).width = 8;
  monthly.getColumn(3).width = 18; monthly.getColumn(3).numFmt = currencyFmt;
  monthly.getColumn(4).width = 20; monthly.getColumn(4).numFmt = currencyFmt;
  monthly.getColumn(5).width = 20; monthly.getColumn(5).numFmt = currencyFmt;
  monthly.getColumn(6).width = 20; monthly.getColumn(6).numFmt = currencyFmt;
  monthly.getColumn(7).width = 12; monthly.getColumn(7).numFmt = '+0.0%;-0.0%';
  monthly.getColumn(8).width = 22; monthly.getColumn(8).numFmt = currencyFmt;
  monthly.getColumn(9).width = 12; monthly.getColumn(9).numFmt = '+0.0%;-0.0%';

  // Helper to style a MoM cell with conditional color
  const styleMomCell = (cell: ExcelJS.Cell, value: number | null) => {
    if (value === null) {
      cell.value = '—';
      cell.numFmt = '@';
      cell.font = { color: { argb: 'FF999999' } };
      return;
    }
    cell.value = value;
    if (value > 0) {
      cell.font = greenFont;
      cell.fill = greenFill;
    } else if (value < 0) {
      cell.font = redFont;
      cell.fill = redFill;
    }
  };

  // Data rows with zebra striping
  let monthlyRowIdx = 5;
  for (let i = 0; i < monthlyRows.length; i++) {
    const row = monthlyRows[i];
    const r = monthly.getRow(monthlyRowIdx++);
    const stripe = i % 2 === 0 ? zebra1 : zebra2;

    r.getCell(1).value = row.month;
    r.getCell(1).font = { bold: true };
    r.getCell(2).value = row.units;
    r.getCell(3).value = row.loanVolume;
    r.getCell(4).value = row.brokeredRevenue;
    r.getCell(5).value = row.bankedNetProfit;
    r.getCell(6).value = row.totalNetProfit;
    r.getCell(8).value = row.avgNetProfitPerLoan;

    // Apply zebra stripe to non-MoM cells
    for (let c = 1; c <= 9; c++) {
      if (c !== 7 && c !== 9) {
        r.getCell(c).fill = stripe;
      }
    }

    // MoM cells with conditional color (overrides stripe)
    styleMomCell(r.getCell(7), row.totalNetProfitMom);
    styleMomCell(r.getCell(9), row.avgNetProfitMom);

    // Thin separator on left of each MoM column
    r.getCell(7).border = { left: { style: 'thin', color: { argb: 'FFCCCCCC' } } };
    r.getCell(9).border = { left: { style: 'thin', color: { argb: 'FFCCCCCC' } } };
  }

  // Total row
  const mt = monthly.getRow(monthlyRowIdx);
  mt.getCell(1).value = 'TOTAL';
  mt.getCell(2).value = monthlyRows.reduce((s, r) => s + r.units, 0);
  mt.getCell(3).value = monthlyRows.reduce((s, r) => s + r.loanVolume, 0);
  mt.getCell(4).value = monthlyRows.reduce((s, r) => s + r.brokeredRevenue, 0);
  mt.getCell(5).value = monthlyRows.reduce((s, r) => s + r.bankedNetProfit, 0);
  mt.getCell(6).value = monthlyRows.reduce((s, r) => s + r.totalNetProfit, 0);
  const totalUnits = monthlyRows.reduce((s, r) => s + r.units, 0);
  const totalNP = monthlyRows.reduce((s, r) => s + r.totalNetProfit, 0);
  mt.getCell(8).value = totalUnits > 0 ? totalNP / totalUnits : 0;
  applyTotalRow(monthly, monthlyRowIdx, moCols);

  // --- Download ---
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `User_Compensation_Company_Revenue_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
